import { useState } from 'react';

const projectTypes = [
  'Hardwood Floor Installation',
  'Hardwood Floor Refinishing',
  'Floor Repair',
  'Floor Replacement',
  'Not Sure — Need Assessment',
];

const trustPoints = [
  { icon: 'ri-shield-check-line', text: 'Licensed & Insured in Maryland' },
  { icon: 'ri-time-line', text: 'Response within 24 hours' },
  { icon: 'ri-money-dollar-circle-line', text: 'Always free, no obligation' },
  { icon: 'ri-star-fill', text: '5-star rated by 500+ homeowners' },
];

export default function LeadFormSection() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    ['name', 'phone', 'project_type'].forEach((f) => {
      const el = form.elements.namedItem(f) as HTMLInputElement | HTMLSelectElement | null;
      if (el) data.append(f, el.value);
    });
    try {
      await fetch('https://readdy.ai/api/form/d6u30of5ehaffuo434ug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-sand py-28 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-3xl overflow-hidden border border-sand-dark">
          <div className="grid lg:grid-cols-2">
            {/* Left: Image + Copy */}
            <div className="relative min-h-96 flex flex-col justify-end p-10 lg:p-14 overflow-hidden">
              <div className="absolute inset-0">
                <img
                  src="https://public.readdy.ai/ai/img_res/39499bd3874d204e7558b524231eaefe.jpg"
                  alt="Premium hardwood flooring Maryland"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-walnut/95 via-walnut/70 to-walnut/30" />
              </div>
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 bg-gold/20 border border-gold/30 rounded-full px-4 py-1.5 mb-6">
                  <div className="w-2 h-2 bg-gold rounded-full animate-pulse" />
                  <span className="font-sans text-gold text-xs font-semibold uppercase tracking-wide">Limited Scheduling Availability</span>
                </div>
                <h2 className="font-serif text-3xl md:text-4xl text-white font-bold leading-tight mb-4">
                  Get Your Free In-Home Estimate Today
                </h2>
                <p className="font-sans text-white/70 text-sm leading-relaxed mb-8">
                  Takes 30 seconds to request. Our hardwood flooring specialists will contact you within 24 hours to schedule your complimentary in-home assessment — no obligation, no pressure.
                </p>
                <div className="space-y-3">
                  {trustPoints.map((p) => (
                    <div key={p.text} className="flex items-center gap-3">
                      <div className="w-7 h-7 flex items-center justify-center bg-gold/20 rounded-lg flex-shrink-0">
                        <i className={`${p.icon} text-gold text-sm`} />
                      </div>
                      <span className="font-sans text-sm text-white/80">{p.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="flex flex-col justify-center p-10 lg:p-14">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 flex items-center justify-center bg-gold/10 rounded-full mx-auto mb-6">
                    <i className="ri-checkbox-circle-line text-gold text-4xl" />
                  </div>
                  <h3 className="font-serif text-3xl text-walnut font-bold mb-3">We&apos;ll Be in Touch!</h3>
                  <p className="font-sans text-charcoal/60 text-base leading-relaxed">
                    Thank you! Our team will contact you within 24 hours to schedule your free in-home estimate.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-8">
                    <h3 className="font-serif text-3xl text-walnut font-bold mb-2">Request Your Free Estimate</h3>
                    <p className="font-sans text-charcoal/60 text-sm">Fill out the form below — our team responds within 24 hours.</p>
                  </div>
                  <form id="homepage-lead-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-walnut font-sans mb-2">
                        Your Full Name <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="e.g. Michael Johnson"
                        className="w-full px-4 py-3.5 rounded-xl border border-sand-dark bg-sand text-sm font-sans focus:outline-none focus:border-oak transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-walnut font-sans mb-2">
                        Phone Number <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="(410) 000-0000"
                        className="w-full px-4 py-3.5 rounded-xl border border-sand-dark bg-sand text-sm font-sans focus:outline-none focus:border-oak transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-walnut font-sans mb-2">
                        Service Needed <span className="text-red-400">*</span>
                      </label>
                      <select
                        name="project_type"
                        required
                        className="w-full px-4 py-3.5 rounded-xl border border-sand-dark bg-sand text-sm font-sans focus:outline-none focus:border-oak transition-colors appearance-none cursor-pointer"
                      >
                        <option value="">Select a service...</option>
                        {projectTypes.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-4 bg-gold text-walnut font-bold rounded-xl hover:bg-gold-dark transition-all text-base font-sans flex items-center justify-center gap-2.5 cursor-pointer whitespace-nowrap disabled:opacity-60"
                    >
                      {loading ? (
                        <><i className="ri-loader-4-line animate-spin" /> Sending Request...</>
                      ) : (
                        <><i className="ri-send-plane-fill" /> Request My Free Estimate</>
                      )}
                    </button>
                    <p className="text-xs text-charcoal/40 font-sans text-center">
                      No spam, no obligation. Serving Maryland homeowners since 2009.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
